import { formatAmount, formatCurrency } from "../../include/money";
import { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { callPostApi } from "../../function/ApiCallFunction";
import { REACT_ENV } from "../../include/config";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import EventEnded from "./EventEnded";

const recentOpens = new Map();

function shouldCountOpen(ticketId) {
  const now = Date.now();
  const last = recentOpens.get(ticketId) || 0;
  if (now - last < 1500) return false;
  recentOpens.set(ticketId, now);
  return true;
}

const fieldSx = {
  "& .MuiInputLabel-root": { fontSize: 13, fontWeight: 650, color: "#111111", position: "static", transform: "none" },
  "& .MuiFormLabel-asterisk": { display: "none" },
  "& .MuiInputBase-root": { borderRadius: 2, background: "#fff", mt: 0.4 },
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
  "& .MuiInputBase-input": { fontSize: 16, py: 1.15 },
};

export default function DrawTicketBuy() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [ended, setEnded] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "", qty: "1" });
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let mounted = true;
    callPostApi("draw-ticket", { mod: "TICKET_PUBLIC", data_arr: { ticket_id: ticketId, count_open: shouldCountOpen(ticketId) ? "1" : "0" } }).then((result) => {
      if (!mounted) return;
      const next = result.data?.ticket || null;
      if (result.status === "410" || next?.ended === "1") setEnded(next);
      else if (result.status === "411" || next?.upcoming === "1") setUpcoming(next);
      else if (result.status === "200") setTicket(next);
      else setMissing(true);
    });
    return () => { mounted = false; };
  }, [ticketId]);

  const maxQty = Number(ticket?.max_qty || 1);
  const price = Number(ticket?.price || 0);
  const qty = Math.min(maxQty, Math.max(1, Number(form.qty) || 1));
  const total = Number((qty * price).toFixed(2));

  const pay = async (event) => {
    event.preventDefault();
    setPaying(true);
    try {
      const result = await callPostApi("draw-ticket", {
        mod: "TICKET_CHECKOUT",
        data_arr: { ...form, qty, ticket_id: ticketId, env: REACT_ENV },
      });
      if (result.status === "410") {
        setEnded(ticket || { title: "" });
        return;
      }
      if (result.status === "411") {
        setUpcoming(ticket || { title: "" });
        return;
      }
      if (result.status === "200" && result.data?.payment_link) {
        window.location.assign(result.data.payment_link);
        return;
      }
      toast.info(result.status_message || result.data?.error || "Could not start the payment");
    } finally {
      setPaying(false);
    }
  };

  if (ended) return <EventEnded title={ended.title} />;
  if (upcoming) return <EventEnded title={upcoming.title} upcoming startsAt={upcoming.start_at} />;

  if (missing) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", background: consoleColors.page, px: 2 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: consoleColors.ink }}>This Lucky Draw Ticket is not available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: { xs: "column", md: "row" }, background: consoleColors.page }}>
      <Box sx={{ width: { xs: "100%", md: "38%" }, background: consoleColors.sidebar, color: "#fff", px: { xs: 2.4, md: 6 }, py: { xs: 3, md: 8 }, display: "flex", flexDirection: "column", justifyContent: { md: "space-between" } }}>
        <Box>
          <Typography sx={{ letterSpacing: 1.6, fontSize: 13, color: consoleColors.accent, fontWeight: 700 }}>CASHI</Typography>
          <Typography sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 700, lineHeight: 1.15, mt: { xs: 2, md: 8 }, maxWidth: 420 }}>{ticket?.title || "Lucky Draw Ticket"}</Typography>
          <Typography sx={{ color: consoleColors.navText, mt: 1.5, fontSize: 16 }}>₹{formatAmount(price)} for each coupon. Buy up to {maxQty} at a time.</Typography>
        </Box>
        <Typography sx={{ display: { xs: "none", md: "block" }, color: consoleColors.navMuted, fontSize: 14 }}>Lucky Draw Ticket</Typography>
      </Box>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center", px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 5 } }}>
        <Box component="form" onSubmit={pay} sx={{ width: "100%", maxWidth: 520, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 3, p: { xs: 2.2, sm: 3 }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.3, alignContent: "start", height: "fit-content" }}>
          {ticket?.banner && (
            <Box
              component={ticket.banner_link ? "a" : "div"}
              href={ticket.banner_link || undefined}
              target={ticket.banner_link ? "_blank" : undefined}
              rel={ticket.banner_link ? "noopener noreferrer" : undefined}
              sx={{ gridColumn: "1 / -1", display: "block", lineHeight: 0, cursor: ticket.banner_link ? "pointer" : "default" }}
            >
              <Box component="img" src={ticket.banner} alt="" sx={{ width: "100%", height: { xs: 92, sm: 110 }, objectFit: "cover", borderRadius: 2 }} />
            </Box>
          )}
          {ticket?.image && <Box component="img" src={ticket.image} alt="" sx={{ width: "100%", height: 180, objectFit: "fill", borderRadius: 2, gridColumn: "1 / -1" }} />}
          <TextField required sx={fieldSx} label="Name" placeholder="Your full name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <TextField required sx={fieldSx} label="Mobile" placeholder="10-digit mobile" value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputProps={{ inputMode: "numeric" }} />
          <TextField sx={fieldSx} label="Email (optional)" placeholder="you@email.com" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
          <TextField required sx={fieldSx} label="Number of tickets" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} inputProps={{ min: 1, max: maxQty }} helperText={`1 to ${maxQty} coupons`} />
          <Box sx={{ gridColumn: "1 / -1", background: "#f3f6f9", borderRadius: 2, px: 1.6, py: 1.3 }}>
            <Typography sx={{ fontSize: 14, color: consoleColors.muted }}>{qty} × ₹{formatAmount(price)}</Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: consoleColors.ink }}>₹{formatAmount(total)}</Typography>
          </Box>
          <Button type="submit" disabled={paying || !ticket} variant="contained" sx={{ ...primaryButtonSx, gridColumn: "1 / -1", minHeight: 48, fontSize: 16 }}>
            {paying ? "Opening payment..." : `Pay ${formatCurrency(total)}`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
